import { useCallback, useMemo, useRef, useState } from 'react'

import getUserLocale from 'get-user-locale'

import {
    getDateFormat,
    getDateStringValue,
    getDateValue,
} from 'features/views/attributes/editable/utils'

import { useAttributeContext } from './useAttributeContext'

export function useDateAttributeEditorState() {
    const {
        field,
        value: attributeValue,
        saveValue,
        clearValue,
        replaceValue,
        discardChanges,
    } = useAttributeContext<string>()
    const { isRequired } = field

    const showTime = field.field.type === 'datetime'
    const timezone = field.field.options?.timezone

    const [value, setValue] = useState(attributeValue)
    const valueRef = useRef(value)
    valueRef.current = value

    const dateValue = useMemo(() => {
        return getDateValue(value, timezone, showTime)
    }, [timezone, value, showTime])

    const locale = getUserLocale()

    const dateFormat = useMemo(() => {
        return getDateFormat(locale, showTime)
    }, [showTime, locale])

    const onChange = useCallback(
        (value: Date) => {
            const formattedDate = getDateStringValue(value, timezone, showTime)
            setValue(formattedDate ?? '')
        },
        [timezone, showTime]
    )

    const onClearValue = useCallback(() => {
        setValue('')
        valueRef.current = ''
        clearValue()
    }, [clearValue])

    const onBlur = useCallback(() => {
        // Use timeout to register inner click events before saving the value.
        setTimeout(() => {
            if (isRequired && !valueRef.current) {
                discardChanges()
                return
            }

            replaceValue(valueRef.current as any)
            saveValue()
        }, 250)
    }, [discardChanges, isRequired, replaceValue, saveValue])

    return useMemo(
        () => ({
            showTime,
            dateFormat,
            value: dateValue,
            onChange,
            onClearValue,
            onBlur,
            isRequired,
        }),
        [showTime, dateFormat, dateValue, onChange, onClearValue, onBlur, isRequired]
    )
}
